import { useEffect, useMemo, useState } from 'react';
import { ErrorLogContext, logError } from '../errors';
import { strictMemo } from '../utils/strict-memo';
import { getAllStorageKeysAndSizes } from './browser-storage';

export type StorageKey<K extends string = string> = `CHRONOSPHERE_${K}`;

interface UseStorageItemOptions<T> {
  /** A function that returns the `Storage` instance to use. Defaults to {@link localStorage} */
  getStorage?: () => Storage;

  /** Optional deserializer method. Defaults to {@link JSON.parse}. */
  parse?: (stringified: string) => T;

  /** Optional serializer method. Defaults to {@link JSON.stringify}. */
  stringify?: (parsed: T) => string;

  /** Optional error handler.  Defaults to {@link logError} */
  onError?: (error: unknown, context?: ErrorLogContext) => void;

  /**
   * When true, the storage value will be limited to just the current tab. When false (or unset), changes made by other
   * tabs or workers will be kept in sync.
   */
  disableExternalSync?: boolean;
}

type UseStorageItemReturnType<T> = [value: T, setValue: (value: T | ((value: T) => T)) => void];

type UseStorageItemType<T> = (() => UseStorageItemReturnType<T>) & {
  /** The internal API for the hook, which is used for testing */
  __internal__: UseStorageItemInternalApi<T>;
};

export interface UseStorageItemInternalApi<T> extends Required<UseStorageItemOptions<T>> {
  /** Get the raw stringified value from storage, or null if not present */
  getStoredValue: () => string | null;

  /** Get the parsed value from storage, or null if not present or parsing fails */
  getParsedValue: () => T | null;

  /** Dispatch an internal `StorageEvent` for sharing values between mount points */
  dispatch: (init: StorageEventInit) => void;

  /** Observe internal `StorageEvent` channel for sharing values between mount points */
  observe: (onStorage: (ev: StorageEvent) => void) => () => void;

  /** Stringify the value and provide it to all observers */
  storeAndDispatch: (valueOrUpdater: T | ((value: T) => T)) => void;

  /**
   * Attempt to parse a stringified value.
   * If the attempt fails (or no value is provided), the `defaultValue` will be returned
   *
   * @param stringified
   * The result of stringification.  The handling of `null` and `undefined`
   * reflects untyped edge cases in `JSON.stringify` and other common serializers.
   */
  tryParse: (stringified: string | null | undefined) => T;

  /**
   * Attempt to stringify a parsed value.
   * If the attempt fails (or no value is produced), `null` will be returned.
   */
  tryStringify: (value: T) => string | null;

  /**
   * Attempt to set the storage item with error handling.
   * Includes storage sizes in error context for debugging.
   */
  trySetStorageItem: (newValue: string) => void;
}

/**
 * Create a new React Hook which gets and sets a rich value in a given storage area.
 *
 * By default, uses `JSON.parse` and `JSON.stringify` alongside `localStorage`.
 *
 * Within a given tab, all components using the returned hook receive the same value,
 * while changes made by other tabs or workers are observed via `StorageEvent` dispatch.
 *
 * @param { StorageKey } key
 * The key of the storage area to read/write
 *
 * @param { T } defaultValue
 * The value to supply if the specified key has never been stored
 *
 * @param { UseStorageItemOptions } [options]
 * Additional options to customize storage interactions and (de)serialization
 *
 * @returns
 * The hook function, which itself returns the observed value and an update callback
 *
 * ## Usage
 *
 * ### ✅ Do: Create hook at module level for shared state across components
 *
 * @example
 * ```typescript
 * // Values will sync and re-render across components
 * const useMyData = createUseStorageItem('CHRONOSPHERE_MY_DATA', { count: 0 });
 *
 * function MyComponent() {
 *   const [data, setData] = useMyData();
 *   return <div>{data.count}</div>;
 * }
 * ```
 *
 * ### ⚠️ Don't: Create hook inside component
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   // Creates a new hook on every render!  Values won't sync or re-render!
 *   const useMyData = createUseStorageItem('CHRONOSPHERE_MY_DATA', { count: 0 });
 *   const [data, setData] = useMyData();
 *   return <div>{data.count}</div>;
 * }
 * ```
 *
 * ## Testing
 *
 * ### ✅ Do: Mock storage using the internal API
 *
 * @example
 * ```typescript
 * const useMyData = createUseStorageItem('CHRONOSPHERE_MY_DATA', { count: 0 });
 *
 * beforeEach(() => {
 *   // Mocks the storage instance for the hook for isolation
 *   jest.spyOn(useMyData.__internal__, 'getStorage').mockReturnValue(new TestStorage());
 * });
 *
 * it('works with mocked storage', () => {
 *   const res = renderHook(useMyData);
 *   // ... safe, isolated test implementation
 * });
 * ```
 *
 * ### ⚠️ Don't: Testing without mocking, as this may pollute shared storage!
 *
 * @example
 * ```typescript
 * const useMyData = createUseStorageItem('CHRONOSPHERE_MY_DATA', { count: 0 });
 *
 * it('will write to real shared storage', () => {
 *   const res = renderHook(useMyData);
 *   // ... unsafe, unisolated test implementation
 * });
 * ```
 *
 * ## Implementation Notes
 *
 * To comply with browser standards and avoid convoluted questions of ownership,
 * we pass as much as possible via the `StorageEvent` interface, with string values.
 *
 * Last-input memoization is used in both directions (`parse` and `stringify`),
 * which reduces computation and storage requirements for large objects, and ensures
 * that all consumers receive the same reference (for non-primative values).
 *
 * ## Known Limitations
 *
 * Due to browser limitations, direct calls to e.g. `localStorage.setItem` are NOT observed,
 * nor will values be shared between two unique hooks created using the same storage key,
 * so it is important to ensure that one hook is created for each key, and used exclusively.
 */

export function createUseStorageItem<T>(
  key: StorageKey,
  defaultValue: T,
  options: UseStorageItemOptions<T> = {}
): UseStorageItemType<T> {
  /** Internal `StorageEvent` Target for sharing values between mount points */
  const target = new EventTarget();

  const disableExternalSync = options.disableExternalSync ?? false;

  const __internal__: UseStorageItemInternalApi<T> = {
    getStorage: options.getStorage ?? getLocalStorage,

    parse: options.parse ?? JSON.parse,

    stringify: options.stringify ?? JSON.stringify,

    onError: options.onError ?? logError,

    disableExternalSync,

    getStoredValue: () => __internal__.getStorage()?.getItem(key) ?? null,

    getParsedValue: () => __internal__.tryParse(__internal__.getStoredValue()),

    dispatch: (init) => {
      target.dispatchEvent(new StorageEvent('storage', init));
    },

    observe: (onStorage) => {
      const upcast = onStorage as EventListenerOrEventListenerObject;
      target.addEventListener('storage', upcast);
      return () => void target.removeEventListener('storage', upcast);
    },

    tryParse: strictMemo((stringified) => {
      if (stringified !== null && stringified !== undefined) {
        try {
          return __internal__.parse(stringified);
        } catch (e) {
          __internal__.onError(e);
        }
      }

      return defaultValue;
    }),

    tryStringify: strictMemo((value) => {
      try {
        return __internal__.stringify(value) ?? null;
      } catch (e) {
        __internal__.onError(e);
        return null;
      }
    }),

    trySetStorageItem: (newValue) => {
      const storage = __internal__.getStorage();
      try {
        storage?.setItem(key, newValue);
      } catch (e) {
        const storageSizes = getAllStorageKeysAndSizes();
        __internal__.onError(e, { contexts: { storage: { storageSizes } } });
      }
    },

    storeAndDispatch: (valueOrUpdater) => {
      const storage = __internal__.getStorage();
      // Check to see if value is an updater function, and create an identity function if not
      const oldValue = storage?.getItem(key) ?? null;
      const newValue = __internal__.tryStringify(
        isFunction(valueOrUpdater) ? valueOrUpdater(__internal__.tryParse(oldValue)) : valueOrUpdater
      );

      if (oldValue == newValue) {
        // value is unchanged
        return;
      }

      if (newValue === null) {
        // value became nullish
        storage?.removeItem(key);
      } else {
        // value is novel
        __internal__.trySetStorageItem(newValue);
      }

      // Dispatch a storage event for all consumers
      __internal__.dispatch({
        key,
        newValue,
        oldValue,
        storageArea: storage instanceof Storage ? storage : null,
      });
    },
  };

  // observe changes "within the context of another document" (other tabs or workers) when that functionality is not
  // intentionally disabled.
  if (!disableExternalSync) {
    window.addEventListener('storage', (ev) => {
      if (ev.key === key && ev.storageArea === __internal__.getStorage()) __internal__.dispatch(ev);
    });
  }

  /** The hook function itself; See {@link UseStorageItemReturnType} */
  const useStorageItem = (): UseStorageItemReturnType<T> => {
    // Track the _stringified_ values, in this context and others
    const [stringified, setStringified] = useState(__internal__.getStoredValue());

    // Compute the parsed value when the storage representation updates
    const parsed = useMemo(() => __internal__.tryParse(stringified), [stringified]);

    // Observe internal events
    useEffect(() => __internal__.observe((ev) => setStringified(ev.newValue)), []);

    // Return the expected tuple
    return [parsed, __internal__.storeAndDispatch];
  };

  // return the hook with exposed internal API for testing
  return Object.assign(useStorageItem, { __internal__ });
}

interface CreateUseStorageJSONOpts<T> extends Pick<UseStorageItemOptions<T>, 'disableExternalSync'> {
  onError?: (error: unknown) => void;
}

/**
 * Create a new React Hook which gets and sets a rich value in a `localStorage`,
 * using `JSON.parse` and `JSON.stringify`. See {@link createUseStorageItem} for details, usage and testing.
 */
export const createUseLocalStorageJSON = <T>(key: StorageKey, value: T, opts?: CreateUseStorageJSONOpts<T>) =>
  createUseStorageItem<T>(key, value, {
    getStorage: () => localStorage,
    parse: JSON.parse,
    stringify: JSON.stringify,
    ...opts,
  });

/**
 * Create a new React Hook which gets and sets a rich value in `sessionStorage`,
 * using `JSON.parse` and `JSON.stringify`. See {@link createUseStorageItem} for details, usage and testing.
 */
export const createUseSessionStorageJSON = <T>(key: StorageKey, value: T, opts?: CreateUseStorageJSONOpts<T>) =>
  createUseStorageItem<T>(key, value, {
    getStorage: () => sessionStorage,
    parse: JSON.parse,
    stringify: JSON.stringify,
    ...opts,
  });

// default return local storage (named for reference stability)
function getLocalStorage() {
  return localStorage;
}

// Type guard to check if an object is a function
function isFunction(obj: unknown): obj is CallableFunction {
  return obj instanceof Function;
}
